import { useEffect, useState } from 'react';
import Splash from './Splash';

const HOMEPAGE_RX = /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/?)?$/;
const SEEN_KEY = 'sl_splash_seen';

function shouldShowSplash(pathname: string): boolean {
  if (typeof window === 'undefined') return false;
  if (window !== window.top) return false;
  if (!HOMEPAGE_RX.test(pathname)) return false;
  try {
    if (window.localStorage.getItem(SEEN_KEY) === '1') return false;
  } catch {
    return true;
  }
  return true;
}

function getSplashTheme(): 'day' | 'night' {
  try {
    const stored = window.localStorage.getItem('theme');
    if (stored === 'dark') return 'night';
    if (stored === 'light') return 'day';
  } catch {
    // Ignore storage access failures and continue through fallbacks.
  }

  if (document.documentElement.classList.contains('dark')) return 'night';
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'night';
  return 'day';
}

interface SplashGateProps {
  appLoading: boolean;
  pathname: string;
}

export const SplashGate = ({ appLoading, pathname }: SplashGateProps) => {
  const [hasMarkedSeen, setHasMarkedSeen] = useState(false);
  const [active] = useState(() => shouldShowSplash(pathname));
  const [theme] = useState(() => getSplashTheme());

  useEffect(() => {
    if (!active || appLoading || hasMarkedSeen) return;
    try {
      window.localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // localStorage unavailable; the splash can still render for this visit.
    }
    setHasMarkedSeen(true);
  }, [active, appLoading, hasMarkedSeen]);

  if (!active) return null;

  return (
    <Splash
      mode={theme}
      message="Loading the atlas"
      show={appLoading}
      minDuration={1200}
    />
  );
};

export default SplashGate;
